import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// How many minutes before expiry to show warning & attempt refresh
const WARN_BEFORE_MINUTES = 5;
const REFRESH_BEFORE_MINUTES = 10;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const expiresAtRef = useRef(
    parseFloat(sessionStorage.getItem('token_expires_at')) || null
  );
  const refreshTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const interceptorIdRef = useRef(null);

  // ---- logout (stable reference) ----
  const logout = useCallback((message) => {
    clearTimeout(refreshTimerRef.current);
    clearTimeout(warnTimerRef.current);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('token_expires_at');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    if (message) {
      toast.error(message);
    }
  }, []);

  // ---- One-time cleanup of legacy localStorage ----
  useEffect(() => {
    localStorage.removeItem('token');
  }, []);

  // ---- Axios 401 interceptor ----
  useEffect(() => {
    // Remove previous interceptor if exists
    if (interceptorIdRef.current !== null) {
      axios.interceptors.response.eject(interceptorIdRef.current);
    }

    interceptorIdRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          // Token expired or became invalid — force logout
          logout('Your session has expired. Please sign in again.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.response.eject(interceptorIdRef.current);
      }
    };
  }, [token, logout]);

  // ---- Silent token refresh ----
  const refreshToken = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/refresh`);
      const { token: newToken, expires_at: newExpiresAt } = response.data;
      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('token_expires_at', String(newExpiresAt));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      expiresAtRef.current = newExpiresAt;
      setToken(newToken);
      scheduleTimers(newExpiresAt);
    } catch (err) {
      // Refresh failed — token is likely already expired
      logout('Your session has expired. Please sign in again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  // ---- Schedule refresh & warning timers based on expiry ----
  const scheduleTimers = useCallback((expiresAt) => {
    clearTimeout(refreshTimerRef.current);
    clearTimeout(warnTimerRef.current);

    if (!expiresAt) return;

    const nowSec = Date.now() / 1000;
    const msUntilExpiry = (expiresAt - nowSec) * 1000;
    const msUntilRefresh = msUntilExpiry - REFRESH_BEFORE_MINUTES * 60 * 1000;
    const msUntilWarn = msUntilExpiry - WARN_BEFORE_MINUTES * 60 * 1000;

    if (msUntilRefresh > 0) {
      refreshTimerRef.current = setTimeout(() => {
        refreshToken();
      }, msUntilRefresh);
    } else if (msUntilExpiry > 0) {
      // Less than REFRESH_BEFORE_MINUTES left — try refresh immediately
      refreshToken();
    }

    if (msUntilWarn > 0) {
      warnTimerRef.current = setTimeout(() => {
        toast.warning('Your session will expire soon. Saving your work is recommended.', {
          duration: 10000,
        });
      }, msUntilWarn);
    }
  }, [refreshToken]);

  // ---- Bootstrap: set auth header & fetch user ----
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      // Schedule timers for existing session
      const storedExpiry = parseFloat(sessionStorage.getItem('token_expires_at'));
      if (storedExpiry) {
        scheduleTimers(storedExpiry);
      }
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---- Cleanup timers on unmount ----
  useEffect(() => {
    return () => {
      clearTimeout(refreshTimerRef.current);
      clearTimeout(warnTimerRef.current);
    };
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, expires_at: expiresAt, user: userData, must_change_password } = response.data;
    sessionStorage.setItem('token', newToken);
    sessionStorage.setItem('token_expires_at', String(expiresAt));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    expiresAtRef.current = expiresAt;
    setToken(newToken);
    setUser(userData);
    setMustChangePassword(must_change_password || false);
    scheduleTimers(expiresAt);
    return { user: userData, must_change_password };
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    const { token: newToken, expires_at: expiresAt, user: userData } = response.data;
    sessionStorage.setItem('token', newToken);
    sessionStorage.setItem('token_expires_at', String(expiresAt));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    expiresAtRef.current = expiresAt;
    setToken(newToken);
    setUser(userData);
    scheduleTimers(expiresAt);
    return userData;
  };

  // Role helpers
  const role = user?.role || 'reader';
  const isAdmin = role === 'admin';
  const isEditor = role === 'editor' || role === 'admin';
  const isReader = true; // all authenticated users can read

  // Permission checks
  const canCreate = isEditor;
  const canUpload = isEditor;
  const canEdit = isEditor;
  const canDelete = isAdmin;
  const canRequestDelete = isEditor && !isAdmin;
  const canManageUsers = isAdmin;
  const canReviewDeletes = isAdmin;

  // Clear the must change password flag (called after successful password change)
  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, fetchUser,
      isAuthenticated: !!user,
      mustChangePassword, clearMustChangePassword,
      role, isAdmin, isEditor, isReader,
      canCreate, canUpload, canEdit, canDelete,
      canRequestDelete, canManageUsers, canReviewDeletes,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
